import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import apiClient from '@/lib/api/apiClient';
import { RENT_RECONCILIATION_KEY } from '@/lib/api/investment-rental-periods';
import { AxiosResponse } from 'axios';
import {
  CreateInvestmentTransactionRequestBody,
  CreateInvestmentTransactionResponse,
  UpdateInvestmentTransactionRequestBody,
  UpdateInvestmentTransactionResponse,
  ListInvestmentTransactionsResponse,
  GetInvestmentTransactionStatsResponse,
  AnalyzeInvestmentTransactionsImportResponse,
  PreviewInvestmentTransactionsImportResponse,
  ConfirmInvestmentTransactionsImportResponse,
  TransactionImportMappingDTO,
  TransactionImportRowOverrideDTO,
  TransactionType,
  TransactionCategory,
  InvoiceTransactionDraftDTO,
  ListInvoiceTransactionDraftsResponse,
  UpdateInvoiceTransactionDraftRequestBody,
  UpdateInvoiceTransactionDraftResponse,
  UploadInvoiceTransactionDraftResponse,
  ConfirmInvoiceTransactionDraftResponse,
  RejectInvoiceTransactionDraftRequestBody,
  GetPortfolioReportResponse,
  GetPortfolioFiscalReportResponse,
  PortfolioFiscalQuarter,
} from '@inversor-hub/types';

export const INVESTMENT_TRANSACTIONS_KEY = 'investment-transactions';
export const INVESTMENT_TRANSACTION_STATS_KEY = 'investment-transaction-stats';
export const INVOICE_TRANSACTION_DRAFTS_KEY = 'invoice-transaction-drafts';
export const PORTFOLIO_REPORT_KEY = 'portfolio-report';
export const PORTFOLIO_FISCAL_REPORT_KEY = 'portfolio-fiscal-report';

interface ListTransactionsFilters {
  type?: TransactionType;
  startDate?: string;
  endDate?: string;
  categories?: TransactionCategory[];
}

export function useGetPortfolioReport(year?: number, country?: string) {
  return useQuery({
    queryKey: [PORTFOLIO_REPORT_KEY, year, country],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (year) params.set('year', String(year));
      if (country) params.set('country', country);
      const query = params.size > 0 ? `?${params.toString()}` : '';
      return apiClient.get<GetPortfolioReportResponse>(`/investments/portfolio-report${query}`);
    },
    select: (response) => response.data.data,
    placeholderData: keepPreviousData,
  });
}

export function useGetPortfolioFiscalReport(year?: number, quarter?: PortfolioFiscalQuarter) {
  return useQuery({
    queryKey: [PORTFOLIO_FISCAL_REPORT_KEY, year, quarter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (year) params.set('year', String(year));
      if (quarter) params.set('quarter', String(quarter));
      const query = params.size > 0 ? `?${params.toString()}` : '';
      return apiClient.get<GetPortfolioFiscalReportResponse>(
        `/investments/portfolio-fiscal-report${query}`
      );
    },
    select: (response) => response.data.data,
    placeholderData: keepPreviousData,
  });
}

export function useListInvestmentTransactions(
  investmentId?: string,
  filters?: ListTransactionsFilters
) {
  return useQuery({
    queryKey: [INVESTMENT_TRANSACTIONS_KEY, investmentId, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.categories?.length) params.append('categories', filters.categories.join(','));

      const queryString = params.toString();
      const url = `/investments/${investmentId}/transactions${queryString ? `?${queryString}` : ''}`;

      return apiClient.get<ListInvestmentTransactionsResponse>(url);
    },
    select: (response) => response.data.data,
    enabled: !!investmentId,
    placeholderData: keepPreviousData,
  });
}

export function useGetInvestmentTransactionStats(
  investmentId?: string,
  filters?: Omit<ListTransactionsFilters, 'categories'>
) {
  return useQuery({
    queryKey: [INVESTMENT_TRANSACTION_STATS_KEY, investmentId, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const queryString = params.toString();
      const url = `/investments/${investmentId}/transactions/stats${queryString ? `?${queryString}` : ''}`;

      return apiClient.get<GetInvestmentTransactionStatsResponse>(url);
    },
    select: (response) => response.data.data,
    enabled: !!investmentId,
    placeholderData: keepPreviousData,
  });
}

export function useCreateInvestmentTransaction(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInvestmentTransactionRequestBody) => {
      const response = await apiClient.post<
        unknown,
        AxiosResponse<CreateInvestmentTransactionResponse>,
        CreateInvestmentTransactionRequestBody
      >(`/investments/${investmentId}/transactions`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_TRANSACTIONS_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_TRANSACTION_STATS_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [RENT_RECONCILIATION_KEY, investmentId] });
    },
  });
}

export function useUpdateInvestmentTransaction(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      transactionId,
      data,
    }: {
      transactionId: string;
      data: UpdateInvestmentTransactionRequestBody;
    }) => {
      const response = await apiClient.patch<
        unknown,
        AxiosResponse<UpdateInvestmentTransactionResponse>,
        UpdateInvestmentTransactionRequestBody
      >(`/investments/${investmentId}/transactions/${transactionId}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_TRANSACTIONS_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_TRANSACTION_STATS_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [RENT_RECONCILIATION_KEY, investmentId] });
    },
  });
}

export function useDeleteInvestmentTransaction(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transactionId: string) => {
      await apiClient.delete(`/investments/${investmentId}/transactions/${transactionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_TRANSACTIONS_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_TRANSACTION_STATS_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [RENT_RECONCILIATION_KEY, investmentId] });
    },
  });
}

export function useUploadTransactionDocument(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, file }: { transactionId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(
        `/investments/${investmentId}/transactions/${transactionId}/document`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_TRANSACTIONS_KEY, investmentId] });
    },
  });
}

export function useDeleteTransactionDocument(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transactionId: string) => {
      await apiClient.delete(`/investments/${investmentId}/transactions/${transactionId}/document`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_TRANSACTIONS_KEY, investmentId] });
    },
  });
}

function getTransactionImportUrl(
  action: 'analyze' | 'preview' | 'confirm',
  investmentId?: string
): string {
  return investmentId
    ? `/investments/${investmentId}/transactions/import/${action}`
    : `/investment-transactions/import/${action}`;
}

export function useAnalyzeInvestmentTransactionsImport(investmentId?: string) {
  return useMutation({
    mutationFn: async ({ file, sheetName }: { file: File; sheetName?: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      if (sheetName) formData.append('sheetName', sheetName);

      const response = await apiClient.post<
        unknown,
        AxiosResponse<AnalyzeInvestmentTransactionsImportResponse>,
        FormData
      >(getTransactionImportUrl('analyze', investmentId), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      return response.data.data;
    },
  });
}

export function usePreviewInvestmentTransactionsImport(investmentId?: string) {
  return useMutation({
    mutationFn: async ({
      file,
      mapping,
      includeDuplicates,
      rowOverrides,
    }: {
      file: File;
      mapping: TransactionImportMappingDTO;
      includeDuplicates: boolean;
      rowOverrides?: TransactionImportRowOverrideDTO[];
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping', JSON.stringify(mapping));
      formData.append('includeDuplicates', String(includeDuplicates));
      if (rowOverrides?.length) {
        formData.append('rowOverrides', JSON.stringify(rowOverrides));
      }

      const response = await apiClient.post<
        unknown,
        AxiosResponse<PreviewInvestmentTransactionsImportResponse>,
        FormData
      >(getTransactionImportUrl('preview', investmentId), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      return response.data.data;
    },
  });
}

export function useConfirmInvestmentTransactionsImport(investmentId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      mapping,
      includeDuplicates,
      rowOverrides,
    }: {
      file: File;
      mapping: TransactionImportMappingDTO;
      includeDuplicates: boolean;
      rowOverrides?: TransactionImportRowOverrideDTO[];
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping', JSON.stringify(mapping));
      formData.append('includeDuplicates', String(includeDuplicates));
      if (rowOverrides?.length) {
        formData.append('rowOverrides', JSON.stringify(rowOverrides));
      }

      const response = await apiClient.post<
        unknown,
        AxiosResponse<ConfirmInvestmentTransactionsImportResponse>,
        FormData
      >(getTransactionImportUrl('confirm', investmentId), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: investmentId
          ? [INVESTMENT_TRANSACTIONS_KEY, investmentId]
          : [INVESTMENT_TRANSACTIONS_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: investmentId
          ? [INVESTMENT_TRANSACTION_STATS_KEY, investmentId]
          : [INVESTMENT_TRANSACTION_STATS_KEY],
      });
    },
  });
}

export function useListInvoiceTransactionDrafts(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [INVOICE_TRANSACTION_DRAFTS_KEY],
    queryFn: async () => {
      return apiClient.get<ListInvoiceTransactionDraftsResponse>('/invoice-transaction-drafts');
    },
    select: (response) => response.data.data,
    enabled: options?.enabled ?? true,
  });
}

export function useUploadInvoiceFile(investmentId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file }: { file: File }): Promise<InvoiceTransactionDraftDTO> => {
      const formData = new FormData();
      formData.append('file', file);

      const url = investmentId
        ? `/investments/${investmentId}/invoice-transaction-drafts/upload`
        : '/invoice-transaction-drafts/upload';

      const response = await apiClient.post<UploadInvoiceTransactionDraftResponse>(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVOICE_TRANSACTION_DRAFTS_KEY] });
    },
  });
}

export function useUpdateInvoiceTransactionDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      draftId,
      data,
    }: {
      draftId: string;
      data: UpdateInvoiceTransactionDraftRequestBody;
    }): Promise<InvoiceTransactionDraftDTO> => {
      const response = await apiClient.patch<
        unknown,
        AxiosResponse<UpdateInvoiceTransactionDraftResponse>,
        UpdateInvoiceTransactionDraftRequestBody
      >(`/invoice-transaction-drafts/${draftId}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVOICE_TRANSACTION_DRAFTS_KEY] });
    },
  });
}

export function useConfirmInvoiceTransactionDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (draftId: string) => {
      const response = await apiClient.post<ConfirmInvoiceTransactionDraftResponse>(
        `/invoice-transaction-drafts/${draftId}/confirm`
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVOICE_TRANSACTION_DRAFTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_TRANSACTIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_TRANSACTION_STATS_KEY] });
    },
  });
}

export function useRejectInvoiceTransactionDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      draftId,
      reason,
    }: {
      draftId: string;
      reason?: string | null;
    }): Promise<InvoiceTransactionDraftDTO> => {
      const response = await apiClient.post<
        unknown,
        AxiosResponse<UpdateInvoiceTransactionDraftResponse>,
        RejectInvoiceTransactionDraftRequestBody
      >(`/invoice-transaction-drafts/${draftId}/reject`, { reason });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVOICE_TRANSACTION_DRAFTS_KEY] });
    },
  });
}
